// Built-ins
import { useState, useEffect } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";
import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../../global_vars";
import { useMergedModalVisibility } from "../../common/useMergedModalVisibility";
import { NESTED_MODAL_PROPS } from "../../common/nestedModalProps";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import AddCountryModal from "./add_country";

// Variables
window.Swal = Swal;

// API endpoints - Using regions API
const ADD_PROVINCE = "https://ultima.icsgr.com/api/regions/all_provinces/";
const GET_COUNTRIES = "https://ultima.icsgr.com/api/regions/all_countries/";

// Helpers
const clampLen = (value, max) => value.slice(0, max);
const toSmallInt = (value) => {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n)) return "";
  return Math.max(-32768, Math.min(32767, n)); // Django SmallIntegerField range
};

function AddProvinceModal({
  defaultCountryId,
  lockCountry = false,
  refreshData,
  onProvinceCreated,
  show: showProp,
  onHide: onHideProp,
  hideTrigger = false,
  nested = false,
}) {
  const { show, open, close } = useMergedModalVisibility(showProp, onHideProp);
  const [countries, setCountries] = useState([]);
  const [addCountryOpen, setAddCountryOpen] = useState(false);

  const [title, setTitle] = useState("");
  const [countryId, setCountryId] = useState(""); // maps to country_id
  const [orderindex, setOrderindex] = useState(""); // required

  const resetForm = () => {
    setTitle("");
    setCountryId("");
    setOrderindex("");
  };

  const handleClose = () => {
    close();
    resetForm();
  };
  const handleShow = () => {
    resetForm();
    if (defaultCountryId) setCountryId(defaultCountryId);
    open();
  };

  const reloadCountries = () => {
    const currentHeaders = {
      ...headers,
      Authorization: "Token " + localStorage.getItem("userToken"),
    };
    return axios
      .get(GET_COUNTRIES, { headers: currentHeaders })
      .then((res) => {
        const countriesData = res.data?.all_countries || [];
        setCountries(countriesData);
        return countriesData;
      })
      .catch(() => setCountries([]));
  };

  const isTitleValid = title.trim().length >= 2 && title.trim().length <= 40;
  const isCountryIdValid = countryId.length > 0;
  const isOrderIndexValid = orderindex !== "" && Number.isInteger(+orderindex);

  const isFormValid = isTitleValid && isCountryIdValid && isOrderIndexValid;
    // Fetch countries for dropdown
  useEffect(() => {
    if (show) {
      reloadCountries();
    }
  }, [show]);

  // When used as a nested/controlled modal (opened by parent), handleShow() is not called.
  // Ensure the locked/default country is pre-selected when the modal becomes visible.
  useEffect(() => {
    if (!show) return;
    if (!defaultCountryId) return;
    if (lockCountry || !countryId) {
      setCountryId(defaultCountryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, defaultCountryId, lockCountry]);

  const createNewProvince = async () => {
    try {
      // Update headers with current token
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const res = await axios({
        method: "post",
        url: ADD_PROVINCE,
        headers: currentHeaders,
        data: {
          title: title.trim(),
          country_id: countryId,
          orderindex: Number(orderindex),
        }
      });

      const created = res?.data;

      // Always close immediately on success, even if callbacks fail.
      handleClose();

      try {
        if (typeof refreshData === "function") refreshData();
      } catch (_) {}

      if (typeof onProvinceCreated === "function") {
        try {
          await Promise.resolve(onProvinceCreated(created));
        } catch (_) {}
      }
    } catch (e) {
      console.log('Error creating province:', e);
      console.log('Error response data:', e?.response?.data);
      
      // Handle different error response formats
      let apiMsg = "Something went wrong while creating the province.";
      
      if (e?.response?.data?.error) {
        // Custom error format from our enhanced error handling
        apiMsg = e.response.data.error;
      } else if (e?.response?.data?.province_id) {
        apiMsg = Array.isArray(e.response.data.province_id) ? e.response.data.province_id[0] : e.response.data.province_id;
      } else if (e?.response?.data?.orderindex) {
        // Serializer validation error for orderindex
        apiMsg = e.response.data.orderindex[0];
      } else if (e?.response?.data?.title) {
        apiMsg = Array.isArray(e.response.data.title) ? e.response.data.title[0] : e.response.data.title;
      } else if (e?.response?.data?.detail) {
        apiMsg = e.response.data.detail;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiMsg,
      });
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
          <BiPlus
            style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }}
          />
          Create new Province
        </Button>
      )}

      <GuardedModal
        show={show}
        size="lg"
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        {...(nested ? NESTED_MODAL_PROPS : {})}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create new Province</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Province:</Form.Label>
                  <Form.Control
                    maxLength={40}
                    placeholder="e.g., ATTICA"
                    onChange={(e) =>
                      setTitle(clampLen(e.target.value.toUpperCase(), 40))
                    }
                    value={title}
                  />
                </Form.Group>

                <SelectWithInlineCreate
                  className="mb-3"
                  label="Country"
                  required
                  value={countryId}
                  onChange={setCountryId}
                  disabled={lockCountry}
                  placeholderOption="Select Country"
                  onAddClick={lockCountry ? undefined : () => setAddCountryOpen(true)}
                  overviewLinkEntity="country"
                >
                  {Array.isArray(countries) && countries.map((country) => (
                    <option key={country.country_id} value={country.country_id} title={country.country_id}>
                      {country.title}
                    </option>
                  ))}
                </SelectWithInlineCreate>

                <Form.Group className="mb-3">
                  <Form.Label>Order by*:</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 1"
                    onChange={(e) => setOrderindex(toSmallInt(e.target.value))}
                    value={orderindex}
                  />
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "red" }}
              >
                {!isTitleValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Title is required (2–40 chars).
                  </li>
                )}
                {!isCountryIdValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Country is required.
                  </li>
                )}
                {!isOrderIndexValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Order by is required and must be an integer.
                  </li>
                )}
              </ul>
            ) : (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "green" }}
              >
                <li>
                  <AiOutlineCheckCircle
                    style={{ fontSize: 18, marginRight: 6 }}
                  />
                  Validated
                </li>
              </ul>
            )}
          </small>

          <Button color="red" onClick={handleClose}>
            Close
          </Button>
          <Button
            color="green"
            onClick={createNewProvince}
            disabled={!isFormValid}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </GuardedModal>

      <AddCountryModal
        nested
        hideTrigger
        show={addCountryOpen}
        onHide={() => setAddCountryOpen(false)}
        onCountryCreated={async (data) => {
          const id = data?.country_id;
          await reloadCountries();
          if (id) setCountryId(id);
          setAddCountryOpen(false);
        }}
      />
    </>
  );
}

export default AddProvinceModal; 